import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { format } from "date-fns-tz";

import theme from "../../../../theme";
import {
  createInvitation,
  createOwnershipTransfer,
  deleteUser,
  fetchUsersAndInvitations,
  updateInvitation,
  updateOwnershipTransfer,
} from "../../../../redux/features/organizationsSlice";
import ConfirmationDialog from "../../../ConfirmationDialog";
import { Timestamp } from "firebase/firestore";

export const UserList = () => {
  const [overflowUserEl, setOverflowUserEl] = useState(null);
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
  const [showDeleteInvitationConfirm, setShowDeleteInvitationConfirm] =
    useState(false);
  const [overflowTargetId, setOverflowTargetId] = useState(null);
  const [overflowInvitationEl, setOverflowInvitationEl] = useState(null);
  const [showDeleteTransferConfirm, setShowDeleteTransferConfirm] =
    useState(false);
  const [transferToCancel, setTransferToCancel] = useState(null);
  const { organization } = useOutletContext();
  const { id: organizationId } = organization || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    if (organizationId) {
      dispatch(fetchUsersAndInvitations({ organizationId }));
    }
  }, [organizationId, dispatch]);

  if (!organization) {
    return null;
  }
  const { users = [], invitations, invitationsMap, ownershipTransfers } =
    organization;

  const handleResend = async () => {
    const invitation = invitationsMap[overflowTargetId];
    if (!invitation) {
      return;
    }
    const { email, roles } = invitation;
    await dispatch(
      createInvitation({
        organizationId,
        values: {
          email,
          roles,
        },
      }),
    );
    dispatch(fetchUsersAndInvitations({ organizationId }));
  };

  const handleResendTransfer = async (transfer) => {
    if (!transfer) {
      return;
    }
    await dispatch(
      createOwnershipTransfer({
        organizationId,
        values: { email: transfer.email },
      }),
    );
    dispatch(fetchUsersAndInvitations({ organizationId }));
  };

  const pendingTransfers = (ownershipTransfers || []).filter(
    (transfer) => !transfer.declinedAt,
  );
  const declinedTransfers = (ownershipTransfers || []).filter(
    (transfer) => transfer.declinedAt,
  );
  const transferDate = (transfer) => {
    const datePattern = "MMMM do, yyyy";
    const timestamp = transfer.declinedAt || transfer.createdAt;
    if (timestamp && timestamp.toDate) {
      return format(timestamp.toDate(), datePattern);
    }
    return "";
  };

  return (
    <Box>
      <ConfirmationDialog
        open={Boolean(showDeleteUserConfirm)}
        title={"Remove User"}
        message={
          "Are you sure you want to remove this user from the organization?"
        }
        handleConfirm={async () => {
          await dispatch(
            deleteUser({
              organizationId,
              userId: overflowTargetId,
            }),
          );
          setShowDeleteUserConfirm(false);
          setOverflowTargetId(null);
        }}
        handleCancel={() => {
          setShowDeleteUserConfirm(false);
          setOverflowTargetId(null);
        }}
      />
      <ConfirmationDialog
        open={Boolean(showDeleteInvitationConfirm)}
        title={"Cancel Invitation"}
        message={"Are you sure you want to cancel this invitation?"}
        handleConfirm={async () => {
          await dispatch(
            updateInvitation({
              organizationId,
              invitationId: overflowTargetId,
              values: { canceledAt: Timestamp.now() },
            }),
          );
          setShowDeleteInvitationConfirm(false);
          setOverflowTargetId(null);
        }}
        handleCancel={() => {
          setShowDeleteInvitationConfirm(false);
          setOverflowTargetId(null);
        }}
      />
      <ConfirmationDialog
        open={Boolean(showDeleteTransferConfirm)}
        title={"Cancel Ownership Transfer"}
        message={"Are you sure you want to cancel this ownership transfer?"}
        handleConfirm={async () => {
          await dispatch(
            updateOwnershipTransfer({
              organizationId,
              transferId: transferToCancel,
              values: { canceledAt: Timestamp.now() },
            }),
          );
          setShowDeleteTransferConfirm(false);
          setTransferToCancel(null);
        }}
        handleCancel={() => {
          setShowDeleteTransferConfirm(false);
          setTransferToCancel(null);
        }}
      />
      <Menu
        sx={{ mt: "45px" }}
        id="menu-user"
        anchorEl={overflowUserEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(overflowUserEl)}
        onClose={() => {
          setOverflowUserEl(null);
        }}
      >
        <MenuItem
          key={"menu-user-delete"}
          onClick={() => {
            setShowDeleteUserConfirm(true);
            setOverflowUserEl(null);
          }}
        >
          <Typography textAlign="center">Remove User</Typography>
        </MenuItem>
      </Menu>

      <Menu
        sx={{ mt: "45px" }}
        id="menu-invitation"
        anchorEl={overflowInvitationEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(overflowInvitationEl)}
        onClose={() => {
          setOverflowInvitationEl(null);
        }}
      >
        <MenuItem
          key={"menu-invitation-resend"}
          onClick={async () => {
            await handleResend();
            setOverflowInvitationEl(null);
          }}
        >
          <Typography textAlign="center">Re-send Invitation</Typography>
        </MenuItem>
        <MenuItem
          key={"menu-invitation-delete"}
          onClick={() => {
            setShowDeleteInvitationConfirm(true);
            setOverflowInvitationEl(null);
          }}
        >
          <Typography textAlign="center">Cancel Invitation</Typography>
        </MenuItem>
      </Menu>

      {pendingTransfers.map((transfer) => (
        <Alert
          key={`pending-transfer-${transfer.id}`}
          severity="warning"
          sx={{ marginTop: "20px" }}
          data-testid="pending-ownership-transfer"
        >
          <Typography fontWeight={700}>Ownership transfer pending</Typography>
          <Typography>
            {transfer.email} has not accepted ownership yet.
            {transferDate(transfer) ? ` Sent ${transferDate(transfer)}.` : ""}
          </Typography>
          <Box marginTop={"12px"} display={"flex"} gap={"12px"}>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => handleResendTransfer(transfer)}
            >
              Re-send
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => {
                setTransferToCancel(transfer.id);
                setShowDeleteTransferConfirm(true);
              }}
            >
              Cancel transfer
            </Button>
          </Box>
        </Alert>
      ))}
      {declinedTransfers.map((transfer) => (
        <Alert
          key={`declined-transfer-${transfer.id}`}
          severity="error"
          sx={{ marginTop: "20px" }}
        >
          <Typography fontWeight={700}>Ownership transfer declined</Typography>
          <Typography>
            {transfer.email} declined the transfer
            {transferDate(transfer) ? ` on ${transferDate(transfer)}` : ""}.
          </Typography>
        </Alert>
      ))}
      <Box paddingTop={"20px"} display={"flex"} alignItems={"center"}>
        <Typography flexGrow="1" variant="h2">
          Team
        </Typography>
        <Button
          onClick={() => {
            navigate("./invite");
          }}
        >
          Invite Team Member
        </Button>
        <Button
          onClick={() => {
            navigate("./transfer");
          }}
        >
          Transfer Ownership
        </Button>
      </Box>
      <Box
        marginTop={"20px"}
        sx={{ backgroundColor: theme.palette.primary.light }}
        padding="10px"
        display={"flex"}
      >
        <Typography paddingLeft={"10px"} flexGrow={"1"}>
          Name
        </Typography>
        <Typography paddingRight={"50px"}>Roles</Typography>
      </Box>
      <Box>
        {users.map(({ displayName, email, id, roles, isSelf }) => {
          const name = displayName?.trim() || email;
          const rolesText = roles
            ?.filter((role) => role.checked)
            .map((role) => role.name)
            .join(", ");
          return (
            <Box
              key={`user-${id}`}
              sx={{ textDecoration: "none" }}
              component={isSelf ? null : Link}
              to={isSelf ? null : `users/${id}`}
            >
              <Box
                paddingTop={"20px"}
                paddingBottom={"20px"}
                display={"flex"}
                alignContent={"center"}
              >
                <Typography
                  flexGrow={"1"}
                  marginLeft={"20px"}
                  color={"text.primary"}
                  alignSelf={"center"}
                >
                  {name}
                </Typography>
                <Typography
                  marginRight={"20px"}
                  color={"text.primary"}
                  alignSelf={"center"}
                >
                  {rolesText}
                </Typography>
                <IconButton
                  disabled={isSelf}
                  onClick={(event) => {
                    event.preventDefault();
                    setOverflowTargetId(id);
                    setOverflowUserEl(event.target);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <Divider />
            </Box>
          );
        })}
        {invitations?.map(({ id, email, createdAt, declinedAt, roles }) => {
          const datePattern = "MMMM do, yyyy";
          let dateText;
          if (createdAt && createdAt.toDate) {
            dateText = format(createdAt.toDate(), datePattern);
          } else if (declinedAt && declinedAt.toDate) {
            dateText = format(declinedAt.toDate(), datePattern);
          } else {
            dateText = "";
          }

          const rolesText = roles
            ?.filter((role) => role.checked)
            .map((role) => role.name)
            .join(", ");
          return (
            <Box
              key={`invitation-${id}`}
              sx={{ textDecoration: "none" }}
              component={Link}
              to={`invitations/${id}`}
            >
              <Box paddingTop={"20px"} paddingBottom={"20px"} display={"flex"}>
                <Box flexGrow={"1"} marginLeft={"20px"}>
                  <Typography color={"text.primary"}>{email}</Typography>
                  {!declinedAt && (
                    <Typography variant="b6" color={"primary.lightText"}>
                      {`Invitation sent ${dateText}`}
                    </Typography>
                  )}
                  {declinedAt && (
                    <Typography variant="b6" color={"error"}>
                      {`Invitation declined ${dateText}`}
                    </Typography>
                  )}
                </Box>
                <Typography
                  marginRight={"20px"}
                  color={"text.primary"}
                  alignSelf={"center"}
                >
                  {rolesText}
                </Typography>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    setOverflowTargetId(id);
                    setOverflowInvitationEl(event.target);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <Divider />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
